import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Calendar, Clock, MapPin, ChevronRight, Trophy, Users, Layers,
  Megaphone, Play, Image, TrendingUp, ArrowLeft, ChevronLeft, Zap,
  AlertTriangle, Video, Star, Settings,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const formatTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const formatDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return { day: d.getDate(), month: d.toLocaleDateString('en-US', { month: 'short' }), weekday: d.toLocaleDateString('en-US', { weekday: 'short' }) };
};

const ProgramHome = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const progRes = await fetch(`${API_URL}/api/programs/by-slug/${slug}`);
        if (!progRes.ok) { setLoading(false); return; }
        const prog = await progRes.json();
        const dashRes = await fetch(`${API_URL}/api/programs/${prog.id}/dashboard`);
        if (dashRes.ok) setData(await dashRes.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data?.program) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-500 text-lg mb-3">Program not found</p>
          <Link to="/teams" className="text-cyan-400 hover:underline text-sm">Browse Programs</Link>
        </div>
      </div>
    );
  }

  const { program, upcoming_games, upcoming_all, recent_results, announcements, featured_media, teams, stats } = data;
  const color = program.primary_color || '#06b6d4';
  const colorBg = `${color}15`;
  const colorBorder = `${color}30`;
  const initials = program.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* ====== HERO HEADER ====== */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${program.secondary_color || '#0a0a0a'} 0%, #0a0a0a 100%)` }}>
        {/* Accent glow */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-[120px] opacity-20" style={{ backgroundColor: color }} />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full blur-[100px] opacity-10" style={{ backgroundColor: color }} />

        <div className="relative max-w-6xl mx-auto px-4 pt-5 pb-8">
          <Link to="/teams" className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-300 text-xs mb-5 transition-colors" data-testid="back-link">
            <ChevronLeft className="w-3 h-3" /> All Programs
          </Link>

          <div className="flex items-start gap-4 sm:gap-5">
            <Avatar className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl shrink-0 shadow-xl" style={{ boxShadow: `0 8px 32px ${color}30` }}>
              {program.logo_url ? (
                <img src={program.logo_url} alt="" className="object-cover rounded-2xl" />
              ) : (
                <AvatarFallback className="rounded-2xl text-xl sm:text-2xl font-black text-white" style={{ backgroundColor: color }}>
                  {initials}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight" data-testid="program-title">
                {program.name}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                {program.sport && (
                  <Badge className="text-[10px] px-2 py-0.5 font-medium border-0" style={{ backgroundColor: colorBg, color }}>
                    {program.sport}
                  </Badge>
                )}
                {program.city && (
                  <span className="flex items-center gap-1 text-xs text-zinc-500">
                    <MapPin className="w-3 h-3" /> {program.city}, {program.state}
                  </span>
                )}
              </div>
              {program.description && (
                <p className="text-xs text-zinc-500 mt-2 line-clamp-2 max-w-lg">{program.description}</p>
              )}
            </div>
          </div>

          {/* Quick Stats Bar */}
          <div className="flex gap-3 sm:gap-5 mt-6 overflow-x-auto pb-1" data-testid="stats-bar">
            {[
              { label: 'Teams', value: stats.total_teams, icon: Layers },
              { label: 'Members', value: stats.total_members, icon: Users },
              { label: 'Upcoming', value: stats.upcoming_count, icon: Calendar },
              { label: 'Events', value: stats.total_events, icon: Trophy },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.06] min-w-fit">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: colorBg }}>
                  <s.icon className="w-4 h-4" style={{ color }} />
                </div>
                <div>
                  <div className="text-white font-bold text-sm leading-none">{s.value}</div>
                  <div className="text-zinc-500 text-[10px] mt-0.5">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ====== DASHBOARD GRID ====== */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

          {/* ====== LEFT COLUMN (8 cols) ====== */}
          <div className="lg:col-span-8 space-y-4">

            {/* UPCOMING GAMES */}
            <Card className="bg-zinc-900/80 border-zinc-800/60 backdrop-blur-sm overflow-hidden" data-testid="upcoming-games-section">
              <div className="h-0.5" style={{ backgroundColor: color }} />
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white font-bold text-base flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: colorBg }}>
                      <Calendar className="w-3.5 h-3.5" style={{ color }} />
                    </div>
                    Upcoming Games
                  </h2>
                  <Link to={`/${slug}/teams`} className="text-xs font-medium hover:underline" style={{ color }} data-testid="view-all-schedule">
                    View Schedule
                  </Link>
                </div>
                {upcoming_games.length === 0 && upcoming_all.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                    <p className="text-zinc-600 text-sm">No upcoming games this week</p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {(upcoming_games.length > 0 ? upcoming_games : upcoming_all).slice(0, 4).map(event => {
                      const d = formatDay(event.start_time);
                      const isGame = event.event_type === 'game';
                      return (
                        <div
                          key={event.id}
                          className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/40 border border-zinc-800/60 hover:border-zinc-700 transition-colors group"
                          data-testid={`upcoming-${event.id}`}
                        >
                          {/* Date block */}
                          <div className="w-12 h-14 rounded-lg flex flex-col items-center justify-center shrink-0" style={{ backgroundColor: colorBg }}>
                            <span className="text-[10px] font-medium uppercase" style={{ color }}>{d.weekday}</span>
                            <span className="text-white font-black text-lg leading-none">{d.day}</span>
                            <span className="text-[9px] text-zinc-500">{d.month}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-semibold text-sm truncate">{event.title}</span>
                              {isGame && event.opponent && (
                                <Badge className="text-[9px] px-1.5 py-0 bg-red-500/10 text-red-400 border-red-800 border">{event.opponent}</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-0.5">
                              <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {formatTime(event.start_time)}</span>
                              {event.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {event.location}</span>}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-zinc-500 transition-colors shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* RECENT RESULTS */}
            {recent_results.length > 0 && (
              <Card className="bg-zinc-900/80 border-zinc-800/60 backdrop-blur-sm overflow-hidden" data-testid="recent-results-section">
                <CardContent className="p-4 sm:p-5">
                  <h2 className="text-white font-bold text-base flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-amber-500/10">
                      <Trophy className="w-3.5 h-3.5 text-amber-400" />
                    </div>
                    Recent Results
                  </h2>
                  <div className="space-y-2">
                    {recent_results.map(game => {
                      const isWin = game.result?.startsWith('W');
                      // Parse score from result string like "W 72-58" or "L 48-55"
                      const scoreMatch = game.result?.match(/[WL]\s*(\d+)\s*-\s*(\d+)/);
                      const scoreDisplay = scoreMatch ? `${scoreMatch[1]}-${scoreMatch[2]}` : game.result;
                      return (
                        <div
                          key={game.id}
                          className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-xl bg-zinc-800/40 border border-zinc-800/60"
                          data-testid={`result-${game.id}`}
                        >
                          {/* W/L badge */}
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center font-black text-xs sm:text-sm shrink-0 ${
                            isWin ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
                          }`}>
                            {isWin ? 'W' : 'L'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-white font-semibold text-xs sm:text-sm block truncate">{game.title}</span>
                            <div className="text-[10px] sm:text-[11px] text-zinc-500 mt-0.5 truncate">
                              {formatDate(game.start_time)} {game.location && `· ${game.location}`}
                            </div>
                          </div>
                          <div className="text-right shrink-0 ml-1">
                            <div className="text-white font-bold text-sm sm:text-base font-mono">
                              {scoreDisplay}
                            </div>
                            <div className={`text-[10px] font-medium ${isWin ? 'text-emerald-400' : 'text-red-400'}`}>
                              {isWin ? 'Victory' : 'Defeat'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ACTIVE TEAMS SNAPSHOT */}
            <Card className="bg-zinc-900/80 border-zinc-800/60 backdrop-blur-sm overflow-hidden" data-testid="teams-snapshot-section">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white font-bold text-base flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: colorBg }}>
                      <Layers className="w-3.5 h-3.5" style={{ color }} />
                    </div>
                    Active Teams
                  </h2>
                  <Link to={`/${slug}/teams`} className="text-xs font-medium hover:underline" style={{ color }}>
                    Manage
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {teams.map(team => (
                    <Link
                      key={team.id}
                      to={`/${slug}/teams/${team.slug || team.id}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/40 border border-zinc-800/60 hover:border-zinc-700 transition-all group"
                      data-testid={`team-snapshot-${team.id}`}
                    >
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-sm text-white shrink-0" style={{ backgroundColor: `${color}25` }}>
                        {team.name?.split(' ').map(w => w[0]).join('').slice(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold text-sm truncate group-hover:text-cyan-300 transition-colors">{team.name}</div>
                        <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-0.5">
                          {team.age_group && <span>{team.age_group}</span>}
                          {team.season && <span>{team.season}</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        {team.record && team.record !== '0-0' && (
                          <div className="font-bold font-mono text-sm" style={{ color }}>{team.record}</div>
                        )}
                        <div className="flex items-center gap-1 text-[10px] text-zinc-500">
                          <Users className="w-3 h-3" /> {team.member_count || 0}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ====== RIGHT COLUMN (4 cols) ====== */}
          <div className="lg:col-span-4 space-y-4">

            {/* ANNOUNCEMENTS */}
            <Card className="bg-zinc-900/80 border-zinc-800/60 backdrop-blur-sm overflow-hidden" data-testid="announcements-section">
              <div className="h-0.5 bg-amber-500" />
              <CardContent className="p-4">
                <h2 className="text-white font-bold text-sm flex items-center gap-2 mb-3">
                  <Megaphone className="w-4 h-4 text-amber-400" /> Announcements
                </h2>
                {announcements.length === 0 ? (
                  <p className="text-zinc-600 text-xs py-6 text-center">No announcements</p>
                ) : (
                  <div className="space-y-3">
                    {announcements.map(a => (
                      <div
                        key={a.id}
                        className={`p-3 rounded-xl border ${
                          a.priority === 'urgent'
                            ? 'bg-red-500/[0.06] border-red-900/40'
                            : 'bg-zinc-800/40 border-zinc-800/60'
                        }`}
                        data-testid={`announcement-${a.id}`}
                      >
                        <div className="flex items-start gap-2 mb-1.5">
                          {a.priority === 'urgent' && <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />}
                          {a.pinned && a.priority !== 'urgent' && <Star className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />}
                          <h3 className="text-white font-semibold text-xs leading-tight">{a.title}</h3>
                        </div>
                        <p className="text-zinc-400 text-[11px] leading-relaxed">{a.content}</p>
                        <div className="flex items-center gap-2 mt-2 text-[10px] text-zinc-600">
                          <span>{a.author_name}</span>
                          <span>·</span>
                          <span>{formatDate(a.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* FEATURED MEDIA */}
            <Card className="bg-zinc-900/80 border-zinc-800/60 backdrop-blur-sm overflow-hidden" data-testid="featured-media-section">
              <CardContent className="p-4">
                <h2 className="text-white font-bold text-sm flex items-center gap-2 mb-3">
                  <Video className="w-4 h-4" style={{ color }} /> Featured Media
                  <Link to={`/${slug}/media`} className="ml-auto text-[10px] font-medium hover:underline" style={{ color }} data-testid="view-all-media">
                    View All
                  </Link>
                </h2>
                {featured_media.length === 0 ? (
                  <div className="text-center py-8">
                    <Image className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                    <p className="text-zinc-600 text-xs">No media yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {featured_media.map(m => (
                      <div
                        key={m.id}
                        className="rounded-xl overflow-hidden bg-zinc-800/60 border border-zinc-800/60 group cursor-pointer"
                        data-testid={`media-${m.id}`}
                      >
                        {/* Media thumbnail / placeholder */}
                        <div className="relative aspect-video bg-zinc-800 flex items-center justify-center">
                          {m.thumbnail_url ? (
                            <img src={m.thumbnail_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              {m.media_type === 'video' ? (
                                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10 group-hover:bg-white/20 transition-colors">
                                  <Play className="w-4 h-4 text-white ml-0.5" />
                                </div>
                              ) : (
                                <Image className="w-6 h-6 text-zinc-600" />
                              )}
                            </div>
                          )}
                          <Badge className="absolute top-2 left-2 text-[9px] px-1.5 py-0 bg-black/60 text-white border-0 backdrop-blur-sm">
                            {m.media_type === 'video' ? 'VIDEO' : 'PHOTO'}
                          </Badge>
                        </div>
                        {m.title && (
                          <div className="p-2.5">
                            <span className="text-white text-xs font-medium line-clamp-1">{m.title}</span>
                            <span className="text-zinc-600 text-[10px] block mt-0.5">{formatDate(m.created_at)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* QUICK LINKS */}
            <Card className="bg-zinc-900/80 border-zinc-800/60 backdrop-blur-sm" data-testid="quick-links">
              <CardContent className="p-4">
                <h2 className="text-white font-bold text-sm mb-3">Quick Links</h2>
                <div className="space-y-1.5">
                  {[
                    { label: 'Full Schedule', to: `/${slug}/teams`, icon: Calendar },
                    { label: 'Team Roster', to: `/${slug}/teams`, icon: Users },
                    { label: 'Team Chat', to: `/${slug}/teams`, icon: Zap },
                    { label: 'Media Gallery', to: `/${slug}/media`, icon: Image },
                    { label: 'Admin Panel', to: `/${slug}/admin`, icon: Settings },
                  ].map(link => (
                    <Link
                      key={link.label}
                      to={link.to}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-zinc-800/60 transition-colors group"
                    >
                      <link.icon className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 transition-colors" />
                      <span className="text-sm text-zinc-400 group-hover:text-white transition-colors flex-1">{link.label}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-zinc-700 group-hover:text-zinc-500 transition-colors" />
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramHome;
